import React from "react";
import { DataTable, Column } from "@/components/ui/data-table";
import { useTranslation } from "react-i18next";
import { RequestFeedback } from "@/services/interfaces/IFeedbackService";
import { NameCell } from "./cells/name";
import { RoomCell } from "./cells/room";
import { DateCell } from "./cells/date";
import { FeedbackCell } from "./cells/feedback";
import { EvaluationsCell } from "./cells/evaluations";
import { ServiceCell } from "./cells/service";
import { AssignedStaffCell } from "./cells/assigned-staff";

type SortOrder = "asc" | "desc" | null;

type Props = {
  data: RequestFeedback[];
  isLoading: boolean;
  sortBy: string | null;
  sortOrder: SortOrder;
  onSort: (field: string) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onRowClick?: (row: RequestFeedback) => void;
};

const RequestsFeedbacksTable: React.FC<Props> = ({
  data,
  isLoading,
  sortBy,
  sortOrder,
  onSort,
  currentPage,
  totalPages,
  onPageChange,
  onRowClick,
}) => {
  const { t } = useTranslation();

  const columns: Column<RequestFeedback>[] = [
    {
      key: "name",
      header: t("feedbacks.table.name"),
      sortable: true,
      className: "text-center",
      render: (_, row) => (
        <NameCell
          name={`${row.request.guest?.profile.firstName || ""} ${row.request.guest?.profile.lastName || ""}`}
          countryCode={row.request.guest?.profile.language || ""}
        />
      ),
    },
    {
      key: "room",
      header: t("feedbacks.table.room"),
      sortable: true,
      className: "text-center",
      render: (_, row) => (
        <RoomCell
          roomNumber={row.request.room?.number || 0}
          roomCategoryName={row.request.room?.category?.name || ""}
        />
      ),
    },
    {
      key: "arrival",
      header: t("feedbacks.table.arrival"),
      sortable: true,
      className: "text-center",
      render: (_, row) => (
        <DateCell date={new Date(row.request.booking?.arrivalDate || "")} />
      ),
    },
    {
      key: "departure",
      header: t("feedbacks.table.departure"),
      sortable: true,
      className: "text-center",
      render: (_, row) => (
        <DateCell date={new Date(row.request.booking?.departureDate || "")} />
      ),
    },

    {
      key: "service",
      header: t("feedbacks.table.service"),
      sortable: true,
      className: "text-center",
      render: (_, row) => (
        <ServiceCell serviceName={row.request.service?.name || ""} />
      ),
    },
    {
      key: "assignedStaff",
      header: t("feedbacks.table.assignedStaff"),
      className: "text-center",
      render: (_, row) => (
        <AssignedStaffCell
          staffData={
            row.request.tasks && (row.request.tasks || []).length > 0
              ? row.request.tasks[0]?.staff?.profile
              : undefined
          }
          teamData={
            row.request.tasks && (row.request.tasks || []).length > 0
              ? row.request.tasks[0]?.team
              : undefined
          }
        />
      ),
    },

    {
      key: "createdAt",
      header: t("feedbacks.table.feedbackDate"),
      sortable: true,
      className: "text-center",
      render: (_, row) => <DateCell date={new Date(row.createdAt || "")} />,
    },
    {
      key: "feedback",
      header: t("feedbacks.table.feedback"),
      sortable: true,
      className: "text-center",
      render: (_, row) => <FeedbackCell feedback={row.comment || ""} />,
    },
    {
      key: "evaluations",
      header: t("feedbacks.table.evaluations"),
      sortable: true,
      className: "text-center",
      render: (_, row) => <EvaluationsCell rating={row.rating || 0} />,
    },
  ];

  return (
    <DataTable<RequestFeedback>
      data={data}
      columns={columns}
      loading={isLoading}
      sorting={{
        field: sortBy ?? null,
        direction: sortOrder ?? null,
        onSort,
      }}
      pagination={{
        currentPage,
        totalPages: totalPages || 1,
        onPageChange,
      }}
      onRowClick={onRowClick}
      withPagination={true}
      withSorting={true}
      emptyMessage={t("feedbacks.table.empty")}
    />
  );
};

export default RequestsFeedbacksTable;
